import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Home as House, FolderOpen as FolderNotchOpen, Share2 as ShareNetwork, TrendingUp as ChartLineUp,
  ShieldCheck, Users as UsersThree, Bell, LogOut as SignOut, Building2 as Buildings,
  Map as MapIcon, Zap as WorkflowIcon, Settings as SettingsIcon
} from "lucide-react";
import OfflineBadge from "@/components/OfflineBadge";
import { useI18n } from "@/context/I18nContext";
import { useBranding } from "@/context/BrandingContext";
import { ROLE_LABELS } from "@/lib/api";

const NAV = [
  { to: "/", label: "Overview", icon: House, roles: null },
  { to: "/cases", label: "Cases", icon: FolderNotchOpen, roles: null },
  { to: "/referrals", label: "Referrals", icon: ShareNetwork, roles: null },
  { to: "/map", label: "GIS Map", icon: MapIcon, roles: null },
  { to: "/analytics", label: "Analytics", icon: ChartLineUp, roles: ["admin","executive","programme_manager","government","partner"] },
  { to: "/workflows", label: "Workflows", icon: WorkflowIcon, roles: ["admin","programme_manager","executive"] },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, roles: ["admin","executive","programme_manager","government"] },
  { to: "/users", label: "Users & Roles", icon: UsersThree, roles: ["admin","executive","programme_manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: null },
];

function LangSwitcher() {
  const { lang, setLang, languages } = useI18n();
  return (
    <select data-testid="lang-switcher" value={lang} onChange={e => setLang(e.target.value)}
      className="border border-slate-200 px-2 py-2 text-xs font-mono bg-white hover:bg-slate-900 hover:text-white">
      {(languages.length ? languages : [{code:"en",name:"English"}]).map(l => (
        <option key={l.code} value={l.code}>{l.code.toUpperCase()} · {l.name}</option>
      ))}
    </select>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { logo_url, organization_name } = useBranding();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) { console.warn("notif fetch failed", e); }
  };
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

  const unread = notifs.filter(n => !n.read).length;

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    loadNotifs();
  };

  const filteredNav = NAV.filter(n => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr] bg-white">
      {/* Sidebar */}
      <aside className="border-r border-slate-200 flex flex-col bg-white sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img src={logo_url} alt={organization_name} className="w-10 h-10 object-contain" />
            <div>
              <div className="font-heading font-extrabold tracking-tight text-lg leading-none">ICOS</div>
              <div className="overline text-[9px] mt-1">{organization_name} · Core OS</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {filteredNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({isActive}) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${isActive
                  ? "border-[#0033A0] bg-slate-50 text-[#0F172A] font-semibold"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-semibold truncate max-w-[140px]">{user?.name}</div>
              <div className="overline text-[9px]">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 border border-slate-200 hover:bg-slate-900 hover:text-white transition-colors"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col min-h-screen">
        <header className="border-b border-slate-200 bg-white flex items-center justify-between px-8 py-4 sticky top-0 z-10">
          <div className="flex items-baseline gap-3">
            <span className="overline">Session</span>
            <span className="text-xs text-slate-500 font-mono">{user?.email}</span>
          </div>
          <div className="flex items-center gap-4">
            <OfflineBadge />
            <LangSwitcher />
            <div className="relative">
            <button
              data-testid="notif-btn"
              onClick={() => setShowNotifs(v => !v)}
              className="relative p-2 border border-slate-200 hover:bg-slate-900 hover:text-white transition-colors"
            >
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-[10px] font-bold px-1 min-w-[16px] h-[16px] flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            {showNotifs && (
              <div className="absolute right-0 top-12 w-80 bg-white border border-slate-200 z-50">
                <div className="border-b border-slate-200 px-4 py-3">
                  <div className="overline">Notifications</div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && <div className="p-4 text-sm text-slate-500">No notifications</div>}
                  {notifs.map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b border-slate-100 text-sm ${n.read ? "opacity-60" : ""}`}>
                      <div className="font-semibold">{n.title}</div>
                      <div className="text-slate-600">{n.body}</div>
                      {!n.read && (
                        <button onClick={() => markRead(n.id)} className="mt-2 text-[10px] overline text-[#0033A0]">
                          Mark read
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        </header>
        <main className="flex-1">
          {children}
        </main>
        <footer className="border-t border-slate-200 px-8 py-4 flex items-center justify-between text-xs text-slate-500">
          <span className="font-mono">ICOS v1.0 · secure session</span>
          <span>ISCIC · Institutional Systems for Child & Community</span>
        </footer>
      </div>
    </div>
  );
}
